import { Injectable } from '@angular/core';
import { limit, orderBy } from '@angular/fire/firestore';
import {
  catchError,
  combineLatest,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { AccountingEntry } from '@models';
import { FirestoreService } from './firestore.service';
import { TenantContextService } from './tenant-context.service';
import { GrantService } from './grant.service';
import { MovementService } from './movement.service';

@Injectable({
  providedIn: 'root',
})
export class AccountingService {
  /** All accounting entries for the current tenant */
  public all$: Observable<AccountingEntry[]>;

  /**
   * Most recent accounting entries for the current tenant, newest first.
   * Meant for dashboard-style previews: consumers slice it down to the few
   * rows they paint. Use `all$` when the whole collection is needed.
   */
  public latestEntries$: Observable<AccountingEntry[]>;

  /**
   * Número de cobros con tarjeta cobrados de verdad (`status === 'SUCCESS'`)
   * de la temporada activa que todavía NO tienen apunte contable, es decir,
   * cuyo `id` no aparece como `movementId` de ningún apunte vivo (`all$`, sin
   * filtro de año). Los cobros `PENDING` y `FAIL` no cuentan.
   * Alimenta el badge de la pestaña de contabilidad y la alerta del home.
   */
  public pendingCardPaymentsCount$: Observable<number>;

  constructor(
    private readonly firestore: FirestoreService,
    private readonly tenantContext: TenantContextService,
    private readonly grantService: GrantService,
    private readonly movementService: MovementService,
  ) {
    this.all$ = this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<AccountingEntry>(
          this.tenantContext.collections.ACCOUNTING,
        ),
      ),
      shareReplay(1),
    );

    this.latestEntries$ = this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<AccountingEntry>(
          this.tenantContext.collections.ACCOUNTING,
          orderBy('date', 'desc'),
          // Over-fetch: `delete` is a soft delete and `listDocuments` drops the
          // `deleted` docs on the client, after the limit has been applied.
          limit(15),
        ),
      ),
      shareReplay(1),
    );

    this.pendingCardPaymentsCount$ = combineLatest([
      this.movementService.incomeMovements$.pipe(catchError(() => of([]))),
      this.all$.pipe(catchError(() => of([] as AccountingEntry[]))),
    ]).pipe(
      map(([movements, entries]) => {
        const linkedIds = new Set(
          entries.map((e) => e.movementId).filter((id): id is string => !!id),
        );
        return movements.filter(
          (m) => m.status === 'SUCCESS' && !!m.id && !linkedIds.has(m.id),
        ).length;
      }),
      catchError(() => of(0)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  getById(id: string): Observable<AccountingEntry> {
    return this.firestore.getDocument<AccountingEntry>(
      this.tenantContext.collections.ACCOUNTING,
      id,
    );
  }

  async create(
    data: Omit<AccountingEntry, 'id' | 'createdAt' | 'updatedAt'>,
  ): Promise<string> {
    const id = await this.firestore.create<AccountingEntry>(
      this.tenantContext.collections.ACCOUNTING,
      data,
    );
    if (data.grantId) await this.recomputeGrantJustified(data.grantId);
    return id;
  }

  async update(data: Partial<AccountingEntry> & { id: string }): Promise<void> {
    await this.firestore.update<AccountingEntry>(
      this.tenantContext.collections.ACCOUNTING,
      data,
    );
    if (data.grantId) await this.recomputeGrantJustified(data.grantId);
  }

  async delete(id: string, grantId?: string): Promise<void> {
    await this.firestore.delete(this.tenantContext.collections.ACCOUNTING, id);
    if (grantId) await this.recomputeGrantJustified(grantId);
  }

  /**
   * Recomputes `amountJustified` on a grant by summing all EXPENDITURE entries
   * linked to it, weighted by their `grantImputationPct`.
   */
  async recomputeGrantJustified(grantId: string): Promise<void> {
    const all = await firstValueFrom(this.all$);
    const total = all
      .filter((e) => e.grantId === grantId && e.type === 'EXPENDITURE')
      .reduce(
        (sum, e) => sum + e.amount * ((e.grantImputationPct ?? 100) / 100),
        0,
      );
    await this.grantService.update({
      id: grantId,
      amountJustified: +total.toFixed(2),
    });
  }
}
